import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Avatar,
    Box,
    Button,
    CircularProgress,
    List,
    ListItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';

const getCsrfToken = () => document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';

const sortByCreatedAt = (a, b) => {
    const first = a?.created_at ? Date.parse(a.created_at) : 0;
    const second = b?.created_at ? Date.parse(b.created_at) : 0;
    return first - second;
};

const parseCount = (value, fallback) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isNaN(parsed) ? fallback : parsed;
};

const AlarmCommentsPanel = ({
    alarmId,
    fetchUrl,
    storeUrl,
    canComment,
    currentUser,
    initialCount,
}) => {
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [commentText, setCommentText] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const csrfToken = useMemo(() => getCsrfToken(), []);
    const counterElement = useMemo(() => {
        if (typeof document === 'undefined') {
            return null;
        }
        return document.getElementById('alarm-comments-counter');
    }, [alarmId]);

    const updateCounter = useCallback((value) => {
        if (!counterElement) {
            return;
        }
        const safeValue = Number.isFinite(value) ? value : 0;
        counterElement.textContent = String(safeValue);
    }, [counterElement]);

    useEffect(() => {
        updateCounter(parseCount(initialCount, 0));
    }, [initialCount, updateCounter]);

    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();

        const loadComments = async () => {
            setLoading(true);
            try {
                const response = await fetch(fetchUrl, {
                    headers: {
                        Accept: 'application/json',
                    },
                    credentials: 'same-origin',
                    signal: controller.signal,
                });

                if (!response.ok) {
                    const payload = await response.json().catch(() => ({}));
                    throw new Error(payload?.message ?? 'Unable to load comments.');
                }

                const payload = await response.json();
                if (!isMounted) {
                    return;
                }

                const items = Array.isArray(payload?.data) ? payload.data : [];
                const sorted = [...items].sort(sortByCreatedAt);
                setComments(sorted);
                setError('');
                updateCounter(parseCount(payload?.comments_count, sorted.length));
            } catch (err) {
                if (err.name === 'AbortError') {
                    return;
                }
                if (isMounted) {
                    setError(err.message ?? 'Unable to load comments.');
                    setComments([]);
                    updateCounter(0);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        loadComments();

        return () => {
            isMounted = false;
            controller.abort();
        };
    }, [fetchUrl, updateCounter]);

    const handleSubmit = useCallback(async (event) => {
        event.preventDefault();
        if (!commentText.trim()) {
            return;
        }

        setSubmitting(true);
        try {
            setError('');
            const response = await fetch(storeUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-CSRF-TOKEN': csrfToken,
                },
                credentials: 'same-origin',
                body: JSON.stringify({ comment: commentText.trim() }),
            });

            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload?.message ?? 'Unable to save comment.');
            }

            const payload = await response.json();
            const newComment = payload?.data;
            const nextComments = newComment
                ? [...comments, newComment].sort(sortByCreatedAt)
                : comments;

            setComments(nextComments);
            setCommentText('');
            updateCounter(parseCount(payload?.comments_count, nextComments.length));
        } catch (err) {
            setError(err.message ?? 'Unable to save comment.');
        } finally {
            setSubmitting(false);
        }
    }, [commentText, comments, csrfToken, storeUrl, updateCounter]);

    return (
        <Box>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {canComment && (
                <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3 }}>
                    <TextField
                        multiline
                        minRows={3}
                        maxRows={8}
                        fullWidth
                        placeholder="Add a comment"
                        value={commentText}
                        onChange={(event) => setCommentText(event.target.value)}
                        disabled={submitting}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="secondary"
                            disableElevation
                            disabled={submitting || !commentText.trim()}
                        >
                            {submitting ? 'Saving...' : 'Post Comment'}
                        </Button>
                    </Box>
                </Box>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2">Loading comments...</Typography>
                </Box>
            ) : comments.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No comments yet.
                </Typography>
            ) : (
                <List disablePadding>
                    {comments.map((comment, index) => {
                        const isLast = index === comments.length - 1;
                        const createdAt = comment?.created_at ? new Date(comment.created_at) : null;
                        const hasValidDate = createdAt && !Number.isNaN(createdAt.getTime());
                        const displayTime = hasValidDate
                            ? createdAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
                            : comment.created_at_time ?? '';
                        const displayDate = hasValidDate
                            ? createdAt.toLocaleDateString()
                            : comment.created_at_date ?? '';
                        const markerSx = {
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            mr: 3,
                            minWidth: 56,
                        };
                        if (!isLast) {
                            markerSx['&::after'] = {
                                content: '""',
                                position: 'absolute',
                                top: 48,
                                bottom: -16,
                                width: '2px',
                                backgroundColor: (theme) => theme.palette.divider,
                            };
                        }

                        const avatarSrc = comment.user?.avatar_url || undefined;
                        const avatarFallback = extractInitials(comment.user?.name ?? comment.user?.email ?? '?');

                        return (
                            <ListItem
                                key={comment.id ?? index}
                                alignItems="flex-start"
                                sx={{ px: 0, pb: isLast ? 0 : 2, display: 'flex', alignItems: 'stretch' }}
                            >
                                <Box sx={markerSx}>
                                    <Avatar
                                        src={avatarSrc}
                                        alt={comment.user?.name ?? 'User avatar'}
                                        sx={{ width: 40, height: 40, fontSize: '0.95rem', bgcolor: avatarSrc ? 'transparent' : 'secondary.main', color: avatarSrc ? undefined : '#fff' }}
                                    >
                                        {!avatarSrc ? avatarFallback : null}
                                    </Avatar>
                                </Box>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Stack direction="row" spacing={3} alignItems="flex-start" flexWrap="wrap">
                                        <Stack spacing={0} sx={{ minWidth: 90 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                {displayTime}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {displayDate}
                                            </Typography>
                                        </Stack>
                                        <Typography variant="subtitle2" color="text.primary">
                                            {comment.user?.name ?? 'Unknown user'}
                                            {currentUser?.email && comment.user?.email === currentUser.email ? ' (you)' : ''}
                                        </Typography>
                                    </Stack>
                                    <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                                        {comment.comment}
                                    </Typography>
                                </Box>
                            </ListItem>
                        );
                    })}
                </List>
            )}
        </Box>
    );
};

AlarmCommentsPanel.propTypes = {
    alarmId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    fetchUrl: PropTypes.string.isRequired,
    storeUrl: PropTypes.string,
    canComment: PropTypes.bool,
    currentUser: PropTypes.shape({
        name: PropTypes.string,
        email: PropTypes.string,
    }),
    initialCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

AlarmCommentsPanel.defaultProps = {
    storeUrl: '',
    canComment: true,
    currentUser: null,
    initialCount: 0,
};

function extractInitials(source) {
    if (!source || typeof source !== 'string') {
        return '?';
    }

    const cleaned = source.trim();
    if (!cleaned) {
        return '?';
    }

    const parts = cleaned.split(/\s+/).slice(0, 2);
    if (parts.length === 1) {
        return parts[0].charAt(0).toUpperCase();
    }

    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
}

export default AlarmCommentsPanel;
